import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useModalSlideUp } from "@/features/useModal"
import { useAtom } from "jotai"
import { SetVerifyAtom, verifyAtom } from "@/pages/AccountPage/atoms/verify"
import ResetPassword from "@/pages/AccountPage/components/Models/ResetPassword"
import { createLogger } from "@/utils/logger"
import { PATHS } from "@/constants/path"
import kycRequiredMethods from "@/features_signal/kyc/kycRequiredMethods"
import kycController from "@/features_signal/kyc/kycController"

const logger = createLogger("useCard")

export type KycStatus = Kyc.Status

interface UseCardProps {
    userInfo: ApiTypes.Fetcher.Result.NewUserInfo
}

export function useCard({ userInfo }: UseCardProps) {
    const navigate = useNavigate()
    const { open: openModal } = useModalSlideUp("account-page")
    const [verifyData] = useAtom(verifyAtom)
    const [, setVerifyData] = useAtom(SetVerifyAtom)

    // Memoized values
    const hasLoginPassword = useMemo(
        () => userInfo?.has_login_password ?? userInfo?.hasLoginPassword,
        [userInfo?.has_login_password, userInfo?.hasLoginPassword],
    )
    const hasEmail = useMemo(() => userInfo?.has_email, [userInfo?.has_email])

    const emailValue = useMemo(() => {
        return userInfo?.has_email ? userInfo.email : "Set"
    }, [userInfo?.has_email, userInfo?.email])

    const phoneNumberValue = useMemo(() => {
        console.log("phoneNumberValue....detail", userInfo)
        const hasPhone = userInfo?.has_phone_number ?? userInfo?.hasPhoneNumber
        if (!hasPhone) return "Set"
        return `+${userInfo.country_code} ${userInfo.phone}`
    }, [userInfo])

    const passwordValue = useMemo(() => {
        const hasPassword = userInfo?.has_login_password ?? userInfo?.hasLoginPassword
        return hasPassword ? "Change" : "Set"
    }, [userInfo?.has_login_password, userInfo?.hasLoginPassword])

    // Handlers
    const handleUserName = useCallback(() => {
        // if (editable) {
        navigate("/s-account/form/username")
        // }
    }, [navigate])

    const handleEmailClick = useCallback(() => {
        if (hasEmail) {
            //目前结论是没有邮箱可以设置，有邮箱了就不可以修改
            // openModal(
            //     <ChangInfo
            //         value={userInfo?.email || ""}
            //         type="email"
            //     />,
            //     {
            //         closeOnClickMask: false,
            //         closeOnEscape: false,
            //     },
            // )
        } else {
            navigate("/s-account/form/email")
            logger.debug("Navigate to email form")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasEmail, userInfo?.email, navigate])

    //手机号不让修改
    // const handlePhoneClick = useCallback(() => {
    //     openModal(
    //         <ChangInfo
    //             value={phoneNumberValue}
    //             type="phone"
    //         />,
    //         {
    //             closeOnClickMask: false,
    //             closeOnEscape: false,
    //         },
    //     )
    // }, [phoneNumberValue, openModal])
    const handlePhoneClick = () => {
        return
    }

    const handlePassword = useCallback(() => {
        if (hasLoginPassword) {
            openModal(<ResetPassword />, {
                closeOnClickMask: true,
                closeOnEscape: false,
            })
            setVerifyData({
                ...verifyData,
                field: "password",
                type: "edit",
            })
        } else {
            setVerifyData({
                ...verifyData,
                field: "password",
                type: "create",
            })
            navigate("/s-account/form/password")
        }
    }, [hasLoginPassword, openModal, setVerifyData, verifyData, navigate])

    const getKYCContent = useCallback(() => {
        const statusMap: Record<KycStatus, unknown> = {
            unset: {
                status: "unset",
                text: "Unset",
                rightButton: {
                    text: "Go To Verify",
                },
                onClick: () => {
                    kycController.resetStage()
                    navigate(PATHS.KYC)
                },
            },
            basic_account: {
                status: "basic_account",
                text: "Basic Account",
                rightButton: {
                    text: "Go To Verify",
                },
                onClick: () => {
                    kycController.resetStage()
                    navigate(PATHS.KYC)
                },
            },
            under_review: {
                status: "under_review",
                text: "Under Review",
            },
            rejected: {
                status: "rejected",
                text: "Rejected",
                onClick: () => {
                    kycController.resetStage()
                    navigate(PATHS.KYC)
                },
            },
            basic_kyc: {
                status: "basic_kyc",
                text: "Basic KYC",
                onClick: () => {
                    kycController.resetStage()
                    navigate(PATHS.KYC)
                },
            },
            fully_kyc: {
                status: "fully_kyc",
                text: "Fully KYC",
            },
            frozen: {
                status: "frozen",
                text: "Frozen",
                onClick: () => {
                    kycController.resetStage()
                    navigate(PATHS.KYC)
                },
            },
            block: {
                status: "block",
                text: "Blocked",
            },
            test_account: {
                status: "test_account",
                text: "Test Account",
                onClick: () => {
                    kycController.resetStage()
                    navigate(PATHS.KYC)
                },
            },
        }

        return statusMap[kycRequiredMethods.kycStatus.peek()] || statusMap.unset
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate])

    const kycContent = useMemo(() => {
        return getKYCContent()
    }, [getKYCContent])

    return {
        hasLoginPassword,
        hasEmail,
        emailValue,
        phoneNumberValue,
        passwordValue,
        kycContent,
        handleUserName,
        handleEmailClick,
        handlePhoneClick,
        handlePassword,
    }
}
